/**
 * The AM-facing name of a catalog entry and of a brief's recommended workflow
 * — ONE rule for every surface of the pane that says "Recommended · <name>".
 *
 * The current-email strip (`ContextStripHost`) and the Ready-to-start cards
 * (`NeedsYouTab` -> `ReadyBriefsQueue`) used to name the same recommendation
 * two ways: the strip from the catalog's registered title, the cards by
 * title-casing the id ("Carrier Mortgagee Update V2"). Story 28633: the
 * registered name, everywhere.
 *
 * The registered `name` wins; an entry without one, or a recommendation the
 * catalog does not hold (or a catalog that has not loaded), falls back to
 * `workflowLabel` — the shared `workflowDisplayName` resolver, never a local
 * title-casing.
 */

import {
  canonicalRecommendedWorkflowId,
  findRecommendedCatalogEntry,
} from "@product-app/common/workflows/launch/launchGuards";
import type { WorkflowCatalogEntry } from "../types/workflow";
import { workflowLabel } from "../components/CatalogCard";

/** A catalog entry's display name: its registered `name`, else the resolver. */
export function catalogLabel(entry: WorkflowCatalogEntry): string {
  return (entry.name || "").trim() || workflowLabel(entry.ref || entry.id);
}

/**
 * The name for a brief's `recommended_workflow_id`. The id is canonicalised
 * first (legacy recommendations remap onto the workflow that replaced them);
 * an exact catalog id wins, a legacy entry that merely remaps onto it only
 * stands in. Null for a blank recommendation.
 */
export function recommendedWorkflowLabel(
  catalog: readonly WorkflowCatalogEntry[],
  recommendedWorkflowId: string | null | undefined,
): string | null {
  const rec = canonicalRecommendedWorkflowId(recommendedWorkflowId ?? null);
  if (!rec) return null;
  const match = findRecommendedCatalogEntry(catalog, rec);
  return match ? catalogLabel(match) : workflowLabel(rec);
}
